import { Caption, Heading } from '@depop/web-thrift-ui/Typography';
import { useTranslations } from 'next-intl';

import styles from './styles.module.css';

import { BagCheckoutButton } from '@/components/Bag/BagCheckoutButton';
import { InlineCampaignBanners } from '@/components/InlineCampaignBanners';
import { Placeholder, PlaceHolderShape } from '@/components/Placeholder';
import { Price } from '@/components/Price';
import { type DetailedBagBanners } from '@/modules/bag/aliases';
import { getNonoptimisticProducts } from '@/modules/bag/helpers';
import {
  isOptimisticSeller,
  hasOptimisticProducts,
  isOptimisticSummary,
  type BagDetailedSellerCache,
} from '@/modules/bag/types';
import { type CurrencyCode } from '@/modules/location/constants';

type Props = {
  seller: BagDetailedSellerCache['seller'];
  unsoldProducts: BagDetailedSellerCache['products'];
  summary: BagDetailedSellerCache['summary'];
  banners?: DetailedBagBanners;
};

function QuickAccessBagPricingAndCheckout({
  seller,
  unsoldProducts,
  summary,
  banners,
}: Props) {
  const tBag = useTranslations('bag');
  const tCommon = useTranslations('common');
  const nonoptimisticUnsoldProducts = getNonoptimisticProducts(unsoldProducts);
  const inclShippingPrice = summary.total_price_components?.shipping_price;

  return (
    <div className={styles.paymentWrapper}>
      {isOptimisticSeller(seller) ||
      isOptimisticSummary(summary) ||
      hasOptimisticProducts(unsoldProducts) ? (
        <>
          <div className={styles.total}>
            <Placeholder
              width="153px"
              height="24px"
              shape={PlaceHolderShape.RECTANGLE}
            />
            <Placeholder
              width="60px"
              height="24px"
              shape={PlaceHolderShape.RECTANGLE}
            />
          </div>
          <Placeholder
            width="100%"
            height="44px"
            shape={PlaceHolderShape.RECTANGLE}
          />
        </>
      ) : (
        <>
          <div className={styles.total}>
            <Heading variant="sm">{tBag('Bag.QuickAccess.Total')}</Heading>
            <div className={styles.totalPriceWrapper}>
              <Price
                className={styles.totalPrice}
                /**
                 * @TECHDEBT - PB 16.6.25 - https://depopmarket.atlassian.net/browse/WEB-7154
                 * CurrencyCode backend type needs updating to a string union to avoid this type cast
                 */
                currencyCode={summary.currency as CurrencyCode}
                price={summary.total_amount}
              />
              {inclShippingPrice && (
                <Caption variant="sm-regular">
                  {tBag('Bag.QuickAccess.IncludesFeesAndShipping')}
                </Caption>
              )}
            </div>
          </div>
          {!inclShippingPrice && (
            <Caption variant="lg-regular" className={styles.shipping}>
              {tCommon('ShippingCalculatedAtCheckout')}
            </Caption>
          )}
          <div className={styles.actionGroup}>
            <InlineCampaignBanners banners={banners} />
            <BagCheckoutButton
              trackingType="quickAccessBag"
              products={nonoptimisticUnsoldProducts}
              seller={seller}
            />
          </div>
        </>
      )}
    </div>
  );
}

export { QuickAccessBagPricingAndCheckout };
